import React from "react";

type ErrorBoundaryState = { hasError: boolean; error?: Error };

// `label` switches to a CONTAINED fallback (a card in-place) instead of the
// full-screen one — use it for per-tab / per-section boundaries so one bad
// render degrades just that section, not the whole app. Pass a changing React
// `key` (e.g. the active tab id) to auto-reset the boundary on navigation.
type ErrorBoundaryProps = React.PropsWithChildren<{ label?: string }>;

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // TODO: wire to telemetry if available
    // eslint-disable-next-line no-console
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Contained (per-section) fallback — keeps the rest of the app alive.
      if (this.props.label) {
        return (
          <div className="m-4 rounded-2xl border border-rose-500/15 bg-rose-500/[0.03] p-6 text-center space-y-3">
            <h2 className="text-sm font-semibold text-foreground/80">{this.props.label} hit a snag</h2>
            <p className="text-[12px] text-foreground/55 max-w-sm mx-auto">
              This section failed to render — the rest of N71 is fine. Refresh to retry.
            </p>
            {this.state.error && (
              <details className="text-left text-[11px] bg-foreground/[0.04] p-2.5 rounded-lg border border-foreground/[0.08] max-w-md mx-auto" style={{ whiteSpace: "pre-wrap" }}>
                <summary className="cursor-pointer text-foreground/50">Error details</summary>
                <div className="mt-1.5 text-foreground/60">{this.state.error.message}</div>
              </details>
            )}
            <button
              className="inline-flex items-center justify-center rounded-md bg-foreground/[0.06] px-3 py-1.5 text-[12px] text-foreground/75 hover:bg-foreground/[0.1] transition"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
          </div>
        );
      }
      return (
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="max-w-lg w-full text-center space-y-3">
            <h1 className="text-2xl font-semibold">Something went wrong</h1>
            <p className="text-muted-foreground">An unexpected error occurred. Please try refreshing the page.</p>
            {this.state.error && (
              <details className="text-left text-xs bg-muted p-3 rounded border" style={{ whiteSpace: 'pre-wrap' }}>
                <summary className="cursor-pointer">Show error details</summary>
                <div className="mt-2">
                  <div><strong>Path:</strong> {typeof window !== 'undefined' ? window.location.pathname : ''}</div>
                  <div><strong>Message:</strong> {this.state.error.message}</div>
                  {this.state.error.stack && (
                    <div className="mt-2">
                      <strong>Stack:</strong>
                      <div className="mt-1 overflow-auto max-h-48">{this.state.error.stack.split('\n').slice(0,8).join('\n')}</div>
                    </div>
                  )}
                </div>
              </details>
            )}
            <button
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-primary-foreground shadow hover:opacity-90"
              onClick={() => window.location.reload()}
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


